import { useEffect, useRef } from "react";

/**
 * Canvas com efeito de rede neural sutil para a barra inferior do rodapé.
 * Nós pulsantes + sinapses + pulsos viajantes nas cores do site.
 */
const FooterNeuralBar = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    let width = 0;
    let height = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);

    const NODE_COUNT = 22;
    const nodes = Array.from({ length: NODE_COUNT }, () => ({
      x: Math.random() * (width || 800),
      y: Math.random() * (height || 80),
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.12,
      r: 1.1 + Math.random() * 1.3,
      phase: Math.random() * Math.PI * 2,
    }));

    const pulses: { from: number; to: number; t: number; speed: number }[] = [];

    const spawnPulse = () => {
      const i = Math.floor(Math.random() * nodes.length);
      let bestJ = -1;
      let bestD = Infinity;
      for (let j = 0; j < nodes.length; j++) {
        if (j === i) continue;
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const d = dx * dx + dy * dy;
        if (d < bestD && d < 140 * 140) {
          bestD = d;
          bestJ = j;
        }
      }
      if (bestJ >= 0) {
        pulses.push({ from: i, to: bestJ, t: 0, speed: 0.012 + Math.random() * 0.014 });
      }
    };

    let pulseTimer = 0;

    const draw = (time: number) => {
      ctx.clearRect(0, 0, width, height);

      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
      }

      // Sinapses
      ctx.lineWidth = 0.8;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            const a = (1 - dist / 130) * 0.35;
            ctx.strokeStyle = `rgba(81, 133, 204, ${a})`;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      // Pulsos viajantes
      for (let p = pulses.length - 1; p >= 0; p--) {
        const pulse = pulses[p];
        pulse.t += pulse.speed;
        if (pulse.t >= 1) {
          pulses.splice(p, 1);
          continue;
        }
        const a = nodes[pulse.from];
        const b = nodes[pulse.to];
        const x = a.x + (b.x - a.x) * pulse.t;
        const y = a.y + (b.y - a.y) * pulse.t;
        const glow = ctx.createRadialGradient(x, y, 0, x, y, 10);
        glow.addColorStop(0, "rgba(155, 220, 240, 0.95)");
        glow.addColorStop(0.4, "rgba(109, 165, 139, 0.5)");
        glow.addColorStop(1, "rgba(109, 165, 139, 0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(x, y, 10, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "rgba(230, 245, 255, 0.95)";
        ctx.beginPath();
        ctx.arc(x, y, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }

      // Nós com pulsação
      for (const n of nodes) {
        const pulseR = n.r + Math.sin(time * 0.0022 + n.phase) * 0.6;
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, pulseR * 5);
        grad.addColorStop(0, "rgba(155, 155, 202, 0.55)");
        grad.addColorStop(1, "rgba(155, 155, 202, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(n.x, n.y, pulseR * 5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "rgba(220, 235, 250, 0.9)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, pulseR, 0, Math.PI * 2);
        ctx.fill();
      }

      pulseTimer++;
      if (pulseTimer > 28) {
        pulseTimer = 0;
        spawnPulse();
      }

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ mixBlendMode: "screen", opacity: 0.75 }}
      aria-hidden="true"
    />
  );
};

export default FooterNeuralBar;
